import { useLayoutEffect, useState } from "react";
import { getOrCreateVisitorId } from "@/libs/analytics";
import { getVariant } from "@/libs/experiments";

/**
 * Returns the assigned variant for an experiment.
 *
 * Usage:
 *   const { variant, isVariant } = useExperiment('pricing_cta_color');
 *   if (isVariant('orange_cta')) return <OrangeCTA />;
 *   return <DefaultCTA />;
 *
 * Returns variant: null if experiment is inactive or doesn't exist.
 *
 * SSR-safe: first client render stays null (matches SSR), then resolves in
 * useLayoutEffect before paint so experiment UI does not flash. Dev overrides
 * (`exp_override_<id>` in localStorage) are picked up on that same pass.
 */
export function useExperiment(experimentId: string) {
  const [variant, setVariant] = useState<string | null>(null);

  useLayoutEffect(() => {
    const visitorId = getOrCreateVisitorId();
    const resolve = () => setVariant(getVariant(experimentId, visitorId));
    resolve();

    // Re-resolve if a dev override changes localStorage (same-tab updates are
    // triggered manually via the `exp-override` custom event; cross-tab via
    // the `storage` event).
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === `exp_override_${experimentId}`) resolve();
    };
    const onOverride = () => resolve();
    window.addEventListener("storage", onStorage);
    window.addEventListener("exp-override", onOverride);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("exp-override", onOverride);
    };
  }, [experimentId]);

  return {
    variant,
    isVariant: (v: string) => variant === v,
    isControl: variant === "control",
    isActive: variant !== null,
  };
}
